import React, { useState } from 'react';
import { useFormik } from 'formik';
import Button from 'ui/elements/buttons/Button';
import FormikTextField from 'ui/elements/form/formik/FormikTextField';
import Dialog, { Content, DialogActions, Title } from 'ui/views/dialogs/Dialog';
import * as yup from 'yup';
import { KpiDetails, DisplayType, ComparisonPeriod, IndicatorType } from 'domain/kpis/types';
import { KPI_TEMPLATES, KpiTemplate } from './kpiTemplates';
import Chip from 'ui/elements/Chip';
import styled from '@emotion/styled';
import { contentSpacing, halfSpacing, quarterSpacing, sectionSpacing } from 'ui/theme/themeConstants';
import { Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import ChevronLeftIcon from 'ui/elements/icons/ChevronLeftIcon';
import { bluePlanetTheme } from 'ui/theme';
import { fontSizeSmall, shape, typography } from 'ui/theme/themeBluePlanet';
import MaxDataPointsSlider from 'domain/kpis/MaxDataPointsSlider';

const newKpiSchema = yup.object().shape({
  name: yup.string().required(),
  unitOfMeasure: yup.string(),
  displayType: yup.string().oneOf(['LineChart', 'BarChart', 'NumberIndicator']).required(),
  trendIndicator: yup.string().oneOf(['Up', 'Down']).required(),
  showXAxis: yup.boolean().required(),
  showYAxis: yup.boolean().required(),
  comparisonEnabled: yup.boolean().required(),
  comparisonPeriod: yup.string().oneOf(['Month', 'Year']).required(),
  visibility: yup.string().oneOf(['Public', 'Private']).required(),
});

// Export the API payload type that's actually sent to the backend
export interface KpiFormValues {
  name: string;
  unitOfMeasure?: string;
  initialDisplays?: Array<{
    displayType: DisplayType;
    trendIndicator: IndicatorType;
    showXAxis?: boolean;
    showYAxis?: boolean;
    comparisonEnabled?: boolean;
    comparisonPeriod?: ComparisonPeriod;
    visibility?: 'Public' | 'Private';
    maxDataPoints?: number;
  }>;
}

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${contentSpacing};
  margin-bottom: ${sectionSpacing};
`;

const TemplateCard = styled.div`
  cursor: pointer;
  padding: ${contentSpacing};
  border: 1px solid ${bluePlanetTheme.bluePlanetPalette.grey.medium};
  border-radius: ${shape.borderRadius}px;
  transition: all 0.2s;

  &:hover {
    border-color: ${bluePlanetTheme.bluePlanetPalette.grey.main};
    background-color: ${bluePlanetTheme.bluePlanetPalette.grey.light};
  }
`;

const TemplateTitle = styled.div`
  font-weight: ${typography.fontWeightLight};
  margin-bottom: ${quarterSpacing};
`;

const TemplateDescription = styled.div`
  font-size: ${fontSizeSmall};
  color: ${bluePlanetTheme.bluePlanetPalette.indigo.main};
  margin-bottom: ${halfSpacing};
`;

const TemplateDetails = styled.div`
  display: flex;
  gap: ${halfSpacing};
  align-items: center;
  flex-wrap: wrap;
`;

interface Props {
  type: 'Add' | 'Edit';
  values?: KpiDetails;
  companyId: number;
  izSubmitting: boolean;
  onSubmit: (values: KpiFormValues) => void;
  onClose: () => void;
}

const getDisplayTypeLabel = (displayType: DisplayType): string => {
  switch (displayType) {
    case 'LineChart':
      return 'Line Chart';
    case 'BarChart':
      return 'Bar Chart';
    case 'NumberIndicator':
      return 'Number Indicator';
  }
};

const getDisplayTypeDescription = (displayType: DisplayType): string => {
  switch (displayType) {
    case 'LineChart':
      return 'Best for showing trends over time';
    case 'BarChart':
      return 'Good for period comparisons';
    case 'NumberIndicator':
      return 'Single value with trend indicator';
  }
};

export default function KpiFormDialog(props: Props) {
  const { onSubmit, onClose, type } = props;
  const [showCustomForm, setShowCustomForm] = useState(type === 'Edit');

  const form = useFormik({
    initialValues: {
      name: props.values?.name ?? '',
      unitOfMeasure: props.values?.unitOfMeasure ?? '',
      displayType: 'LineChart' as DisplayType,
      trendIndicator: 'Up' as IndicatorType,
      showXAxis: true,
      showYAxis: true,
      comparisonEnabled: true,
      comparisonPeriod: 'Month' as ComparisonPeriod,
      visibility: 'Private' as 'Public' | 'Private',
      maxDataPoints: 12,
    },
    onSubmit: values => {
      // Transform form values to API format
      const apiPayload: KpiFormValues = {
        name: values.name,
        unitOfMeasure: values.unitOfMeasure,
        initialDisplays: [
          {
            displayType: values.displayType,
            trendIndicator: values.trendIndicator,
            ...(values.displayType !== 'NumberIndicator' && {
              showXAxis: values.showXAxis,
              showYAxis: values.showYAxis,
            }),
            comparisonEnabled: values.comparisonEnabled,
            comparisonPeriod: values.comparisonPeriod,
            visibility: values.visibility,
            ...(values.displayType === 'BarChart' && {
              maxDataPoints: values.maxDataPoints,
            }),
          },
        ],
      };
      onSubmit(apiPayload);
    },
    validationSchema: newKpiSchema,
  });

  const handleTemplateSelect = (template: KpiTemplate) => {
    form.setValues({
      name: template.name,
      unitOfMeasure: template.unitOfMeasure,
      displayType: template.displayType,
      trendIndicator: template.trendIndicator,
      showXAxis: template.showXAxis,
      showYAxis: template.showYAxis,
      comparisonEnabled: template.comparisonEnabled,
      comparisonPeriod: template.comparisonPeriod,
      visibility: template.visibility,
      maxDataPoints: template.displayType === 'BarChart' ? 12 : 12,
    });
    setShowCustomForm(true);
  };

  return (
    <Dialog onClose={onClose} open mobileLayout="fullscreen">
      <Title onClose={onClose}>{type === 'Add' ? 'Add new KPI' : 'Update KPI'}</Title>
      <Content>
        {type === 'Add' && !showCustomForm && (
          <>
            <div className="u-content-spacing-bottom">
              <h4 className="text-h4 u-half-spacing-bottom">Choose from templates</h4>
              <p className="text-body">Select a common KPI to get started quickly, or create your own custom KPI.</p>
            </div>
            <TemplateGrid>
              {KPI_TEMPLATES.map(template => (
                <TemplateCard key={template.name} onClick={() => handleTemplateSelect(template)}>
                  <TemplateTitle>{template.name}</TemplateTitle>
                  {template.description && <TemplateDescription>{template.description}</TemplateDescription>}
                  <TemplateDetails>
                    <Chip color="blue-light" label={getDisplayTypeLabel(template.displayType)} fontSize="small" />
                  </TemplateDetails>
                </TemplateCard>
              ))}
            </TemplateGrid>
          </>
        )}

        {(showCustomForm || type === 'Edit') && (
          <>
            {type === 'Add' && (
              <div className="u-half-spacing-bottom">
                <Button
                  kind="tertiary"
                  size="small"
                  onClick={() => {
                    setShowCustomForm(false);
                    form.resetForm();
                  }}
                >
                  <ChevronLeftIcon fontSize="small" className="u-quarter-spacing-right" />
                  Back to templates
                </Button>
              </div>
            )}
            <FormikTextField
              autoFocus
              className="u-content-spacing-top u-half-spacing-bottom"
              name="name"
              formikProps={form}
              label="Name"
              placeholder="Revenue"
              required
            />
            <FormikTextField
              className="u-half-spacing-bottom"
              name="unitOfMeasure"
              formikProps={form}
              label="Unit of measure"
              placeholder="$"
            />
            <FormControl component="fieldset" className="u-content-spacing-top">
              <FormLabel component="legend">Display Type *</FormLabel>
              <RadioGroup
                name="displayType"
                value={form.values.displayType}
                onChange={e => {
                  const newDisplayType = e.target.value as DisplayType;
                  form.setFieldValue('displayType', newDisplayType);
                  // For BarChart, always use Year comparison
                  if (newDisplayType === 'BarChart') {
                    form.setFieldValue('comparisonPeriod', 'Year');
                  }
                }}
              >
                {(['LineChart', 'BarChart', 'NumberIndicator'] as DisplayType[]).map(displayType => (
                  <FormControlLabel
                    key={displayType}
                    value={displayType}
                    control={<Radio />}
                    label={
                      <div>
                        <div style={{ fontWeight: 500 }}>{getDisplayTypeLabel(displayType)}</div>
                        <div style={{ fontSize: '0.875rem', color: '#666' }}>
                          {getDisplayTypeDescription(displayType)}
                        </div>
                      </div>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <FormControl component="fieldset" className="u-content-spacing-top">
              <FormLabel component="legend">Visibility</FormLabel>
              <RadioGroup
                name="visibility"
                value={form.values.visibility}
                onChange={e => form.setFieldValue('visibility', e.target.value)}
              >
                <FormControlLabel value="Private" control={<Radio />} label="Shareholders and board members" />
                <FormControlLabel value="Public" control={<Radio />} label="Anyone" />
              </RadioGroup>
            </FormControl>

            {/* Max Data Points slider for BarChart */}
            {form.values.displayType === 'BarChart' && (
              <MaxDataPointsSlider
                value={form.values.maxDataPoints}
                onChange={value => form.setFieldValue('maxDataPoints', value)}
                className="u-content-spacing-top"
              />
            )}
          </>
        )}
      </Content>
      {type === 'Add' && !showCustomForm && (
        <DialogActions>
          <div className="u-content-spacing-top">
            <Button kind="tertiary" onClick={() => setShowCustomForm(true)}>
              Start with blank template
            </Button>
          </div>
        </DialogActions>
      )}
      {(showCustomForm || type === 'Edit') && (
        <DialogActions>
          <Button onClick={() => form.handleSubmit()} kind="primary" isLoading={props.izSubmitting}>
            {type === 'Add' ? 'Add' : 'Update'}
          </Button>
          <Button kind="tertiary" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
